// Centralized hook for all team-related data and operations
// Idea: Could potentially combine with useRoomPlayers or extract common functionality

import { useMemo, useCallback, useEffect } from 'react';
import { useRoomState } from './useRoomState';
import { useRoomStateSelector } from './useRoomStateSelector';
import { useRoomInteractionWindow } from './useRoomInteractionWindow';
import {
  useRoomStoreMyPlayer,
  useRoomStoreMyTeam,
} from '../contexts/roomStateStore';
import { RoomTeam, RoomPlayer, GameElement } from '@airhead-games/shared/types';

interface TeamSelectionOptions {
  property?: keyof RoomTeam;
  sort?: 'asc' | 'desc';
  sortField?: keyof RoomTeam;
  filterInclude?: RoomTeam[keyof RoomTeam][];
  filterExclude?: RoomTeam[keyof RoomTeam][];
  filterFrom?: keyof RoomTeam;
}

type SelectTeamData = {
  <T extends keyof RoomTeam>(
    options: TeamSelectionOptions & { property: T }
  ): RoomTeam[T][];
  (options?: TeamSelectionOptions & { property?: undefined }): RoomTeam[];
};

interface UseRoomTeamsReturn {
  // Core data access
  teams: RoomTeam[];
  teamsAssigned: RoomTeam[];
  teamsWaitedOn: RoomTeam[];
  teamCount: number;

  // My team essentials
  myTeam: RoomTeam | null;
  setMyTeam: (team: RoomTeam | null) => void;
  myTeamAssigned: boolean;
  myTeamWaitedOn: boolean;
  myTeamInRoom: boolean;
  myTeamWindowElements: GameElement[];

  // Helper functions
  isMyTeam: (teamId: string) => boolean;
  getTeam: (teamId: string) => RoomTeam | undefined;
  getTeamMembers: (teamId: string) => RoomPlayer[];
  selectTeamData: SelectTeamData;
}

export function useRoomTeams(): UseRoomTeamsReturn {
  const { setMyTeam } = useRoomState();
  // Read myPlayer/myTeam from the tearing-safe store (not the context) so they
  // stay consistent with the room-state slices selected below.
  const myPlayer = useRoomStoreMyPlayer();
  const myTeam = useRoomStoreMyTeam();
  const teams = useRoomStateSelector((state) =>
    state?.teams ? Object.values(state.teams) : []
  );
  const players = useRoomStateSelector((state) =>
    state?.players ? Object.values(state.players) : []
  );
  const teamCount = teams.length;
  const { windowElements, windowWaitingFor } = useRoomInteractionWindow();

  const getTeam = useCallback(
    (teamId: string): RoomTeam | undefined => {
      return teams.find((team) => team.teamId === teamId);
    },
    [teams]
  );

  // Automatically sync myTeam from myPlayer's teamId
  // Note: RoomStateContext handles syncing when team data changes, this just handles initial sync
  useEffect(() => {
    if (myPlayer?.teamId) {
      const team = getTeam(myPlayer.teamId);
      setMyTeam(team || null);
    } else {
      setMyTeam(null);
    }
  }, [myPlayer?.teamId, getTeam, setMyTeam]);

  const myTeamInRoom = useMemo(() => {
    if (!myTeam?.teamId) return false;
    return getTeam(myTeam.teamId) !== undefined;
  }, [myTeam, getTeam]);

  const myTeamAssigned = useMemo(() => {
    if (!myTeam || !windowElements) {
      return false;
    }
    return windowElements.some((element) => {
      return element.assignedTeamId === myTeam.teamId;
    });
  }, [myTeam, windowElements]);

  const myTeamWaitedOn = useMemo(() => {
    if (!myTeam) return false;
    return windowWaitingFor.includes(myTeam.teamId);
  }, [myTeam, windowWaitingFor]);

  // Window elements assigned to my team
  const myTeamWindowElements = useMemo(() => {
    if (!myTeam || !windowElements) return [];
    return windowElements.filter(
      (element) => element.assignedTeamId === myTeam.teamId
    );
  }, [myTeam, windowElements]);

  // Teams assigned to current interaction window
  const teamsAssigned = useMemo(() => {
    if (!windowElements) return [];
    const assignedTeamIds = windowElements
      .map((element) => element.assignedTeamId)
      .filter((teamId): teamId is string => Boolean(teamId));

    return assignedTeamIds
      .map((teamId) => getTeam(teamId))
      .filter((team): team is RoomTeam => Boolean(team));
  }, [windowElements, getTeam]);

  // Teams being waited on
  const teamsWaitedOn = useMemo(() => {
    return windowWaitingFor
      .map((teamId) => getTeam(teamId))
      .filter((team): team is RoomTeam => Boolean(team));
  }, [windowWaitingFor, getTeam]);

  // Generic selectTeamData function
  const selectTeamData = useMemo<SelectTeamData>(() => {
    function select<T extends keyof RoomTeam>(
      options: TeamSelectionOptions & { property: T }
    ): RoomTeam[T][];
    function select(
      options?: TeamSelectionOptions & { property?: undefined }
    ): RoomTeam[];
    function select(
      options: TeamSelectionOptions = {}
    ): RoomTeam[] | RoomTeam[keyof RoomTeam][] {
      let filteredTeams = teams;

      // Apply filtering if specified
      const fieldToFilter = options?.filterFrom;
      if (fieldToFilter && (options?.filterInclude || options?.filterExclude)) {
        // Guard against conflicting filters - prefer filterInclude over filterExclude
        if (options?.filterInclude && options.filterInclude.length > 0) {
          if (options?.filterExclude && options.filterExclude.length > 0) {
            console.warn(
              'selectTeamData: Both filterInclude and filterExclude specified. Using filterInclude only.'
            );
          }
          // Apply include filter only
          filteredTeams = filteredTeams.filter((team) => {
            const fieldValue = team[fieldToFilter];
            return options.filterInclude?.includes(fieldValue) ?? false;
          });
        } else if (options?.filterExclude && options.filterExclude.length > 0) {
          // Apply exclude filter only
          filteredTeams = filteredTeams.filter((team) => {
            const fieldValue = team[fieldToFilter];
            return !(options.filterExclude?.includes(fieldValue) ?? false);
          });
        }
      }

      // Apply sorting if specified
      const { sort, sortField } = options;
      if (sort && sortField) {
        filteredTeams = [...filteredTeams].sort((a, b) => {
          const aValue = a[sortField];
          const bValue = b[sortField];

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sort === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sort === 'asc' ? aValue - bValue : bValue - aValue;
          }
          // For other types, convert to string for comparison
          const aStr = String(aValue);
          const bStr = String(bValue);
          return sort === 'asc'
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        });
      }

      // If no property specified, return sorted/filtered team objects
      const { property } = options;
      if (property === undefined) {
        return filteredTeams;
      }

      // Extract the requested property values
      return filteredTeams.map((team) => team[property]);
    }

    return select;
  }, [teams]);

  const getTeamMembers = useCallback(
    (teamId: string): RoomPlayer[] => {
      const team = getTeam(teamId);
      if (!team) return [];

      return team.teamPlayerIds
        .map((playerId) => players.find((p) => p.playerId === playerId))
        .filter((player): player is RoomPlayer => Boolean(player));
    },
    [getTeam, players]
  );

  const isMyTeam = useCallback(
    (teamId: string) => {
      return myTeam?.teamId === teamId;
    },
    [myTeam]
  );

  return {
    teams,
    teamsAssigned,
    teamsWaitedOn,
    teamCount,
    setMyTeam,
    myTeam,
    myTeamAssigned,
    myTeamWaitedOn,
    myTeamInRoom,
    myTeamWindowElements,
    isMyTeam,
    getTeam,
    getTeamMembers,
    selectTeamData,
  };
}
